import React, { useEffect, useState } from 'react';
import {
    CheckCircleFilled,
    ExclamationCircleFilled,
    EyeOutlined,
    MedicineBoxOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import CasesAPI from '../../../../axios/case/CasesApi';
import {
    LogCard as LogCardWrapper,
    LogCardLeft,
    LogCardIcon,
    LogCardInfo,
    LogCardPatientName,
    LogCardMeta,
    LogCardCategory,
    LogCardDot,
    LogCardMedication,
    LogCardMedicationName,
    LogCardMedicationDetails,
    LogCardMedError,
    LogCardPatientMessage,
    LogCardRight,
    LogCardDate,
    ViewButton,
    SkeletonCircle,
    SkeletonText,
    SkeletonButton,
    LogCardRequestTypeBadge,
} from './styles';

import { RequestTypeEnum } from '../../../../types/enums';

const stripHtml = (html) => {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
};

const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
};

const RX_STATUS = {
    EMPTY: 'empty',
    FETCH_FAILED: 'fetch_failed',
    TIMED_OUT: 'timed_out',
};

const RX_TIMEOUT_MS = 6000;

const isTimeoutError = (err) =>
    err.code === 'ECONNABORTED' || err.message?.toLowerCase().includes('timeout');

const fetchWithRetry = async (caseId) => {
    const attempt = () => CasesAPI.getCasePrescriptions(caseId, { timeout: RX_TIMEOUT_MS });
    try {
        return await attempt();
    } catch (firstErr) {
        if (!isTimeoutError(firstErr)) throw firstErr;
        // One re-attempt on timeout
        return attempt();
    }
};

const LogCardComponent = ({ log }) => {
    const [prescriptions, setPrescriptions] = useState(null);
    const [rxLoading, setRxLoading] = useState(true);
    const [rxStatus, setRxStatus] = useState(null);

    const patientName = [log.patientFirstName, log.patientLastName].filter(Boolean).join(' ') || 'Unknown Patient';

    useEffect(() => {
        let cancelled = false;

        const fetchRx = async () => {
            setRxLoading(true);
            setRxStatus(null);
            try {
                const response = await fetchWithRetry(log._id);
                if (cancelled) return;
                const meds = (response.data?.payload || [])
                    .flatMap(p => p.prescribedMedicine || [])
                    .sort((a, b) => new Date(b.writtenDate || 0) - new Date(a.writtenDate || 0));
                setPrescriptions(meds);
                if (meds.length === 0) setRxStatus(RX_STATUS.EMPTY);
            } catch (err) {
                if (!cancelled) {
                    setPrescriptions([]);
                    setRxStatus(isTimeoutError(err) ? RX_STATUS.TIMED_OUT : RX_STATUS.FETCH_FAILED);
                }
            } finally {
                if (!cancelled) setRxLoading(false);
            }
        };

        fetchRx();
        return () => { cancelled = true; };
    }, [log._id]);

    const isError = !rxLoading && rxStatus !== null;

    if (rxLoading) {
        return (
            <LogCardWrapper $loading>
                <LogCardLeft>
                    <SkeletonCircle />
                    <LogCardInfo>
                        <SkeletonText $h="14px" $w="160px" />
                        <SkeletonText $h="11px" $w="220px" style={{ marginTop: '4px' }} />
                        <SkeletonText $h="11px" $w="180px" style={{ marginTop: '4px' }} />
                    </LogCardInfo>
                </LogCardLeft>
                <LogCardRight>
                    <SkeletonText $h="11px" $w="110px" />
                    <SkeletonButton />
                </LogCardRight>
            </LogCardWrapper>
        );
    }

    const medicationDisplay = () => {
        if (rxStatus === RX_STATUS.TIMED_OUT) {
            return (
                <LogCardMedError>
                    <WarningOutlined />
                    Rx lookup timed out — check the chart
                </LogCardMedError>
            );
        }
        if (rxStatus === RX_STATUS.FETCH_FAILED) {
            return (
                <LogCardMedError>
                    <WarningOutlined />
                    Unable to retrieve Rx — check the chart
                </LogCardMedError>
            );
        }
        if (rxStatus === RX_STATUS.EMPTY) {
            return (
                <LogCardMedError>
                    <WarningOutlined />
                    No confirmed Rx found in DoseSpot
                </LogCardMedError>
            );
        }
        const med = prescriptions[0];
        const name = med.drugName || med.name || 'Unknown medication';
        const details = [
            med.strength,
            med.pillCount ? `Qty: ${med.pillCount}` : null,
            med.refills != null ? `${med.refills} refill${med.refills !== 1 ? 's' : ''}` : null,
            med.writtenDate ? `Written ${formatDate(med.writtenDate)}` : null,
            med.prescriberName ? `Prescribed by ${med.prescriberName}` : null,
        ].filter(Boolean).join(' · ');

        return (
            <>
                <MedicineBoxOutlined />
                <LogCardMedicationName>{name}</LogCardMedicationName>
                {details && <LogCardMedicationDetails> · {details}</LogCardMedicationDetails>}
            </>
        );
    };

    const handleViewCase = () => {
        window.open(`/dashboard/physician/caseview/${log._id}?one-click-prescribe=true`, '_blank');
    };

    const patientMessage = log.reason ? stripHtml(log.reason) : null;

    return (
        <LogCardWrapper $error={isError}>
            <LogCardLeft>
                <LogCardIcon $error={isError}>
                    {isError ? <ExclamationCircleFilled /> : <CheckCircleFilled />}
                </LogCardIcon>
                <LogCardInfo>
                    <LogCardPatientName $error={isError}>{patientName}</LogCardPatientName>
                    <LogCardMeta $error={isError}>
                        <LogCardCategory $error={isError}>{log.categoryName || 'Unknown Program'}</LogCardCategory>
                        {log.requestType === RequestTypeEnum.RX_RENEWAL && (
                            <>
                                <LogCardDot $error={isError}>·</LogCardDot>
                                <LogCardRequestTypeBadge>Renewal</LogCardRequestTypeBadge>
                            </>
                        )}
                        {log.patientDob && (
                            <>
                                <LogCardDot $error={isError}>·</LogCardDot>
                                <span>DOB {formatDate(log.patientDob)}</span>
                            </>
                        )}
                    </LogCardMeta>
                    <LogCardMedication $error={isError}>
                        {medicationDisplay()}
                    </LogCardMedication>
                    {patientMessage && (
                        <LogCardPatientMessage $error={isError} title={patientMessage}>
                            "{patientMessage}"
                        </LogCardPatientMessage>
                    )}
                </LogCardInfo>
            </LogCardLeft>
            <LogCardRight>
                <LogCardDate $error={isError}>Completed {formatDate(log.completedAt)}</LogCardDate>
                <ViewButton $error={isError} onClick={handleViewCase}>
                    <EyeOutlined />
                    View Chart
                </ViewButton>
            </LogCardRight>
        </LogCardWrapper>
    );
};

export default LogCardComponent;
